import React from 'react';
import PropTypes from 'prop-types';
import { graphql, StaticQuery } from 'gatsby';
import CookieConsent from 'react-cookie-consent';

import Container from '../Container';
import Link from '../Link';

import { FooterInfoBar } from './footerInfoBar';
import {
  StyledFooter,
  Logo,
  TopRow,
  SocialLinks,
  BottomRow,
  StyledBottomContainer,
  LeftContainer,
  LinksAndBadgesWrapper,
  CenterContainer,
  RightContainer,
  Column,
  Badge,
  Heading,
  StyledLink,
} from './styles';

// * image {String} src for the logo
// * socialShare {arr} array of data for each social icon
// * menu {arr} array of links for the footer
// * pageLinks {arr} array of links at the bottom of the footer
// * certifications {arr} array of badges for the footer

export function Footer({ data }) {
  if (!data) return null;

  const {
    image,
    imageSharp,
    socialShare,
    menu,
    pageLinks,
    certifications,
    lang,
    footerInfoBGColor,
    footerInfoFontColor,
    footerInfoTextHtml,
    footerInfoCtaStyle,
    footerInfoCtaStyle2,
    footerInfoCtaText,
    footerInfoCtaText2,
    footerInfoCtaUrl,
    footerInfoCtaUrl2,
  } = data;
  return (
    <StyledFooter>
      {/* Inline styling for overwriting the CookieConsent build-in styling */}
      <CookieConsent
        expires={14}
        contentStyle={{ margin: '0' }}
        style={{
          background: footerInfoBGColor,
          color: '#000',
          textAlign: 'center',
          margin: '0',
        }}
        contentClasses="footerTextContent"
        buttonWrapperClasses="footerButtons"
        buttonStyle={{
          background: footerInfoBGColor,
          margin: '0',
          padding: '0',
          display: 'flex',
        }}
        buttonText={
          <>
            <StyledLink
              className={`${footerInfoCtaStyle}`}
              footerInfoFontColor={footerInfoFontColor}
              lang={lang}
            >
              {footerInfoCtaText}
            </StyledLink>
            <StyledLink
              className={`${footerInfoCtaStyle2}`}
              footerInfoFontColor={footerInfoFontColor}
              lang={lang}
            >
              {footerInfoCtaText2}
            </StyledLink>
          </>
        }
        hideOnAccept="true"
        location="bottom"
      >
        <FooterInfoBar
          background={footerInfoBGColor}
          footerInfoFontColor={footerInfoFontColor}
          textHtml={footerInfoTextHtml}
          ctaText={footerInfoCtaText}
          ctaUrl={footerInfoCtaUrl.url}
          ctaStyle={footerInfoCtaStyle}
          ctaText2={footerInfoCtaText2}
          ctaUrl2={footerInfoCtaUrl2 ? footerInfoCtaUrl2.url : undefined}
          ctaStyle2={footerInfoCtaStyle2}
          lang={lang}
        />
      </CookieConsent>

      <Container className="auto-margin">
        <TopRow>
          <LeftContainer>
            {imageSharp && (
              <Link to="/" lang={lang}>
                <Logo src={imageSharp.publicURL} alt={image.altTag} />
              </Link>
            )}
            <SocialLinks>
              {socialShare.map(
                ({
                  link,
                  imageIcon,
                  imageIconSharp,
                  queryString,
                  ariaLabel,
                }) => (
                  <Link
                    key={`social-share-${link.url}`}
                    to={link.url}
                    queryString={queryString}
                    ariaLabel={ariaLabel}
                    lang={lang}
                  >
                    <img
                      src={imageIconSharp.publicURL}
                      alt={imageIcon.altTag}
                    />
                  </Link>
                )
              )}
            </SocialLinks>
          </LeftContainer>
          <LinksAndBadgesWrapper>
            {menu && (
              <CenterContainer>
                {menu.map(({ text, subitems }, index) => (
                  <Column key={`columns-${index.toString()}`}>
                    <Heading>{text}</Heading>
                    {subitems.map(
                      ({ text: linkText, url, queryString, ariaLabel }) => (
                        <Link
                          key={`${linkText}-${url}`}
                          to={`${url}/`}
                          queryString={queryString}
                          ariaLabel={ariaLabel}
                          lang={lang}
                        >
                          {linkText}
                        </Link>
                      )
                    )}
                  </Column>
                ))}
              </CenterContainer>
            )}
            <RightContainer className="no-bullets">
              {certifications.map(
                ({ image: badge, imageSharp: badgeSharp }) => (
                  <li key={`badge-${badge.url}`}>
                    <Badge src={badgeSharp} alt={badge.altTag} />
                  </li>
                )
              )}
            </RightContainer>
          </LinksAndBadgesWrapper>
        </TopRow>
      </Container>
      <BottomRow>
        <StyledBottomContainer className="auto-margin">
          {pageLinks.map(({ text, link, queryString, ariaLabel }) => (
            <Link
              key={`${text}-${link}`}
              to={`${link}/`}
              queryString={queryString}
              ariaLabel={ariaLabel}
              external
              lang={lang}
            >
              {text}
            </Link>
          ))}
        </StyledBottomContainer>
      </BottomRow>
    </StyledFooter>
  );
}

Footer.propTypes = {
  data: PropTypes.shape().isRequired,
  lang: PropTypes.string,
};

Footer.defaultProps = { lang: 'en-ca' };

const withStaticQueryFooter = ({ lang }) => (
  <StaticQuery
    query={graphql`
      query footerQuery {
        prodigy {
          headerFooter {
            footer {
              lang
              image {
                url
                altTag
              }
              imageSharp {
                publicURL
              }
              socialShare {
                link {
                  url
                }
                queryString
                ariaLabel
                imageIcon {
                  url
                  altTag
                }
                imageIconSharp {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      quality: 90
                      placeholder: BLURRED
                      layout: FULL_WIDTH
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
              menu {
                type
                text
                url
                subitems {
                  type
                  text
                  url
                  queryString
                  ariaLabel
                }
              }
              pageLinks {
                text
                link
                ariaLabel
                queryString
              }
              certifications {
                link {
                  url
                }
                image {
                  url
                  altTag
                }
                imageSharp {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      quality: 90
                      placeholder: BLURRED
                      layout: FULL_WIDTH
                      formats: [AUTO, WEBP]
                    )
                  }
                }
                ariaLabel
                queryString
              }
              footerInfoBGColor
              footerInfoFontColor
              footerInfoTextHtml
              footerInfoCtaStyle
              footerInfoCtaStyle2
              footerInfoCtaText
              footerInfoCtaText2
              footerInfoCtaUrl {
                url
              }
              footerInfoCtaUrl2 {
                url
              }
            }
          }
        }
      }
    `}
    render={({ prodigy }) => {
      const { footer } = prodigy.headerFooter.find(
        ({ footer: footerData }) =>
          footerData.lang ===
          /* the footer/header has 2 versions of the site, this logic determines which version to use based on the locale
            of which there are several (en,en-ca,en-us,x-default,etc), therefore only render the indian version when it's locale matches, otherwise always use the english ver */
          (lang !== 'en-in' ? 'en-ca' : 'en-in')
      );
      return <Footer data={footer} />;
    }}
  />
);

export default withStaticQueryFooter;
